<script lang="ts">
  import { fly } from 'svelte/transition';
  import { toastStore } from '$lib/toast/toast.svelte';
</script>

<!-- The bare toast: a single-line toast anchored top-right. Only one shows at a
     time (a new one replaces the previous; see the store): the new one flies in
     from the top while the outgoing one fades off to the left, the two briefly
     overlapping. It has no positioning of its own — both the global <Toasts />
     and any page <ToastOutlet /> wrap this and decide where it sits. -->
<div class="toastStack" aria-live="polite">
  {#each toastStore.toasts as toast (toast.id)}
    <!-- The slot anchors the toast; the inner .toast owns the enter/leave
         transition, so Svelte's transform doesn't fight the slot's positioning. -->
    <div class="slot">
      <button
        class="toast {toast.kind}"
        type="button"
        title="Dismiss"
        onclick={() => toastStore.remove(toast.id)}
        in:fly={{ y: -8, duration: 200 }}
        out:fly={{ x: -8, duration: 200 }}
      >
        <span class="icon"></span>
        <span class="message">{toast.message}</span>
      </button>
    </div>
  {/each}
</div>

<style lang="scss">
  .toastStack {
    position: relative;
  }

  // The toast is anchored top-right; an outgoing one and its replacement share
  // the anchor and cross-fade in place.
  .slot {
    position: absolute;
    top: 0;
    right: 0;
    transform-origin: top right;
    pointer-events: none;
  }

  .toast {
    pointer-events: auto;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;

    border-radius: var(--border-radius-medium);
    background-color: var(--background-site);

    color: var(--text-secondary);
    font-family: var(--font-primary);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    white-space: nowrap;

    .icon {
      @include icon;
      flex-shrink: 0;
    }

    // Green check for success/info, red x for errors.
    &.success .icon,
    &.info .icon {
      --icon-url: url('$lib/icons/check.svg');
      --icon-color: var(--success-bright);
      --icon-width: 14px;
      --icon-height: 11px;
    }
    &.error .icon {
      --icon-url: url('$lib/icons/close.svg');
      --icon-color: var(--warning-bright);
      --icon-width: 12px;
      --icon-height: 12px;
    }

    .message {
      flex: 1;
      text-align: left;
    }
  }
</style>
